import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField/TextField';
import Grid from '@material-ui/core/Grid';
import _ from 'lodash';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';

const styles = (theme) => ({
  Grid: {
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
  MuiFormControlRoot: {
    marginTop: 0,
  },
  outlinedInput: {
    padding: 14,
    fontSize: 15,
    [theme.breakpoints.down('xs')]: {
      padding: 19,
    },
  },
  outlinedLabel: {
    fontSize: 15,
  },
});

const getErrorMessage = (error) => {
  return error ? t(error) : null;
};

const validateTextInput = (name, value, required) => {
  const emailRegexp =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

  if (required && value === '') {
    return 'receiveOfferForm.errors.required';
  }

  if (name === 'email' && !emailRegexp.test(value)) {
    return 'receiveOfferForm.errors.invalidEmail';
  }

  return null;
};

class ReceiveOfferForm extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
    noteLabel: PropTypes.string,
    notice: PropTypes.string,
    handleChange: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      customerID: {
        value: '',
        error: null,
        required: false,
      },
      email: {
        value: '',
        error: null,
        required: true,
      },
      name: {
        value: '',
        error: null,
        required: true,
      },
      company: {
        value: '',
        error: null,
        required: true,
      },
      phonenumber: {
        value: '',
        error: null,
        required: true,
      },
      zip: {
        value: '',
        error: null,
        required: true,
      },
      city: {
        value: '',
        error: null,
        required: true,
      },
      street: {
        value: '',
        error: null,
        required: true,
      },
      country: {
        value: '',
        error: null,
        required: true,
      },
    };
    this.inputRefs = {};
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.setInputRef = this.setInputRef.bind(this);
    this.designApprovalRef = React.createRef();
  }

  setInputRef = (ref) => {
    if (ref) {
      this.inputRefs[ref.name] = ref;
    }
  };

  handleChange(event) {
    const { name, type, checked, value } = event.target;
    this.setState((state) => ({
      [name]: {
        ...state[name],
        value: type === 'checkbox' ? checked : value,
        error: null,
      },
    }));
  }

  handleBlur(event) {
    const value = _.cloneDeep(this.state[event.target.name]);

    value.error = validateTextInput(event.target.name, event.target.value, value.required);
    this.setState({
      [event.target.name]: value,
    });
  }

  validate() {
    let formErrors = false;
    let approvalNotSet = null;
    const values = {};

    const state = _.cloneDeep(this.state);

    const inputFields = [
      'customerID',
      'email',
      'name',
      'company',
      'phonenumber',
      'zip',
      'city',
      'street',
      'country',
    ];

    let errorInputRef = null;
    for (const inputField of inputFields) {
      values[inputField] = state[inputField].value;

      const error = validateTextInput(inputField, state[inputField].value, state[inputField].required);

      if (error !== null) {
        formErrors = true;
      }

      if (error && !errorInputRef) {
        errorInputRef = this.inputRefs[inputField];
      }

      state[inputField].error = error;
    }

    this.setState(state);
    const hasError = formErrors || approvalNotSet === true;

    if (hasError) {
      const result = { hasError, formErrors };

      if (approvalNotSet !== null) {
        result.approvalNotSet = approvalNotSet;
      }

      return result;
    }

    return { hasError, ...values };
  }

  render() {
    const { classes, noteLabel, notice, handleChange } = this.props;

    const { customerID, email, name, company, phonenumber, zip, city, street, country } = this.state;

    return (
      <React.Fragment>
        <TextField
          name="customerID"
          inputRef={this.setInputRef}
          value={customerID.value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          label={t('receiveOfferForm.customerNumber') + (customerID.required ? '*' : '')}
          margin="normal"
          variant="outlined"
          fullWidth
          error={!!customerID.error}
          helperText={getErrorMessage(customerID.error)}
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
          classes={{
            root: classes.MuiFormControlRoot,
          }}
        />
        <TextField
          name="email"
          inputRef={this.setInputRef}
          value={email.value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          label={t('receiveOfferForm.email') + (email.required ? '*' : '')}
          margin="normal"
          variant="outlined"
          fullWidth
          error={!!email.error}
          helperText={getErrorMessage(email.error)}
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
        />
        <TextField
          name="name"
          inputRef={this.setInputRef}
          value={name.value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          label={t('receiveOfferForm.name') + (name.required ? '*' : '')}
          margin="normal"
          variant="outlined"
          fullWidth
          error={!!name.error}
          helperText={getErrorMessage(name.error)}
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
        />
        <TextField
          name="company"
          inputRef={this.setInputRef}
          value={company.value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          label={t('receiveOfferForm.company') + (company.required ? '*' : '')}
          margin="normal"
          variant="outlined"
          fullWidth
          error={!!company.error}
          helperText={getErrorMessage(company.error)}
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
        />
        <TextField
          name="phonenumber"
          inputRef={this.setInputRef}
          value={phonenumber.value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          label={t('receiveOfferForm.phonenumber') + (phonenumber.required ? '*' : '')}
          margin="normal"
          variant="outlined"
          fullWidth
          error={!!phonenumber.error}
          helperText={getErrorMessage(phonenumber.error)}
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
        />
        <Grid container>
          <Grid item xs={12} sm={4} className={classes.Grid}>
            <TextField
              name="zip"
              inputRef={this.setInputRef}
              value={zip.value}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              label={t('receiveOfferForm.zip') + (zip.required ? '*' : '')}
              margin="normal"
              variant="outlined"
              fullWidth
              error={!!zip.error}
              helperText={getErrorMessage(zip.error)}
              InputProps={{
                classes: {
                  input: classes.outlinedInput,
                },
              }}
              InputLabelProps={{
                classes: {
                  outlined: classes.outlinedLabel,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              name="city"
              inputRef={this.setInputRef}
              value={city.value}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              label={t('receiveOfferForm.city') + (city.required ? '*' : '')}
              margin="normal"
              variant="outlined"
              fullWidth
              error={!!city.error}
              helperText={getErrorMessage(city.error)}
              InputProps={{
                classes: {
                  input: classes.outlinedInput,
                },
              }}
              InputLabelProps={{
                classes: {
                  outlined: classes.outlinedLabel,
                },
              }}
            />
          </Grid>
        </Grid>
        <TextField
          name="street"
          inputRef={this.setInputRef}
          value={street.value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          label={t('receiveOfferForm.street') + (street.required ? '*' : '')}
          margin="normal"
          variant="outlined"
          fullWidth
          error={!!street.error}
          helperText={getErrorMessage(street.error)}
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
        />
        <TextField
          name="country"
          inputRef={this.setInputRef}
          value={country.value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          label={t('receiveOfferForm.country') + (country.required ? '*' : '')}
          margin="normal"
          variant="outlined"
          fullWidth
          error={!!country.error}
          helperText={getErrorMessage(country.error)}
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
        />
        <TextField
          label={noteLabel}
          multiline
          rows="3"
          rowsMax="3"
          value={notice}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          fullWidth
          InputProps={{
            classes: {
              input: classes.outlinedInput,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.outlinedLabel,
            },
          }}
        />
      </React.Fragment>
    );
  }
}

export default withStyles(styles, { name: 'ReceiveOfferForm' })(ReceiveOfferForm);
